import React, { useEffect, useMemo, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { Dislike, Like1, TickCircle, Translate } from 'iconsax-react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { formateDesignSystemDate } from 'utils/dateUtils'

import { POST_EMAIL_FEEDBACK_ASYNC } from 'containers/SpearPhishingAgent/constants'
import { selectSimulationData } from 'containers/SpearPhishingAgent/selectors'

import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'
import Skeleton from 'components/lib/DesignSystem/V3/Skeleton'
import {
  PSIMA_PENDO_FEATURES,
  trackPsimaFeedbackSubmitted,
} from 'components/pages/SpearPhishingAgent/psimaPendo'

const EmailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: var(--t-spacing-2-5);
  margin-bottom: var(--t-spacing-8);
`

const UserInfo = styled.div`
  display: flex;
  gap: var(--t-spacing-2-5);
  align-items: center;
`

const UserShortName = styled.div`
  border-radius: 50%;
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--t-colors-base-white);
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-medium);
`

const UserName = styled.div`
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--psima-email-preview-user-name);
  margin-bottom: var(--t-spacing-0-5);
`

const UserEmail = styled.div`
  font-size: var(--t-typography-font-size-xs);
  font-weight: var(--t-typography-font-weight-light);
  color: var(--sub-text-color);
`

const UserSendingDate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--t-spacing-1);
  font-size: var(--t-typography-font-size-xs);
  color: var(--sub-text-color);
`

/* The language this copy went out in (IC-50525). Plain secondary text, not a badge, per the
   decision to keep language as ordinary text everywhere on this screen. */
const SentLanguage = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-1);

  svg {
    flex-shrink: 0;
  }
`

const EmailTitle = styled.div`
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-semi-bold);
  color: var(--color-text);
  margin-bottom: var(--t-spacing-6);
`

const EmailBody = styled.div`
  font-size: var(--t-typography-font-size-base);
  font-weight: var(--t-typography-font-weight-regular);
  color: var(--color-text);
  margin-bottom: var(--t-spacing-6);
`

/* Sits in the drawer footer: the ask on the left, the two answers on the right. */
const EmailFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--t-spacing-4);
  width: 100%;
  text-align: left;
`

const EmailFooterCopy = styled.div`
  min-width: 0;
`

const EmailFooterTitle = styled.div`
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-semi-bold);
  color: var(--color-text);
  margin-bottom: var(--t-spacing-0-5);
`

const EmailFooterDescription = styled.div`
  font-size: var(--t-typography-font-size-xs);
  color: var(--color-text-secondary);
`

/* After a rating: one quiet line where the two buttons were, with a way back. */
const RatedLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--t-spacing-2);
  min-height: 40px;
  font-size: var(--t-typography-font-size-sm);
  color: var(--color-text-secondary);

  svg {
    flex-shrink: 0;
    color: var(--positive-color);
  }

  strong {
    font-weight: var(--t-typography-font-weight-medium);
    color: var(--color-text);
  }
`

const ChangeRating = styled.button`
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--primary);

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`

const THANKS_MS = 2000

const RATING_LABELS = { up: 'Good simulation', down: 'Needs improvement' }

const EmailFooterButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-2);
  flex-shrink: 0;
`

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export { NAV_ACTIONS as PARTICIPANTS_NAV_ACTIONS } from '../../../../navActions'

/** Drawer footer for the email-review view: rate the simulation email. */
export const EmailPreviewFooter = ({ activeParticipant }) => {
  const dispatch = useDispatch()
  const {
    emailPreviewLoader,
    emailFeedbackLoader,
    emailFeedbackPending,
    emailFeedbackByParticipant,
  } = useSelector(selectSimulationData)

  const ratingKey = activeParticipant
    ? `${activeParticipant.campaignId}:${activeParticipant.id}`
    : null
  const rating = ratingKey ? emailFeedbackByParticipant?.[ratingKey] : null
  const pendingFeedback =
    emailFeedbackPending?.key === ratingKey ? emailFeedbackPending.feedback : null

  /* "Thanks, noted" for a moment right after the click, then the quiet rated line. */
  const [justRated, setJustRated] = useState(false)
  const [changing, setChanging] = useState(false)
  const previousRating = useRef(rating)

  useEffect(() => {
    if (rating && !previousRating.current) {
      setJustRated(true)
      const timer = setTimeout(() => setJustRated(false), THANKS_MS)
      previousRating.current = rating
      return () => clearTimeout(timer)
    }
    previousRating.current = rating
    return undefined
  }, [rating])

  useEffect(() => {
    setChanging(false)
    setJustRated(false)
  }, [ratingKey])

  const handleFeedback = (feedback) => {
    if (!activeParticipant) {
      return
    }

    trackPsimaFeedbackSubmitted({
      target: 'email',
      reviewSide: feedback ? 'up' : 'down',
    })

    setChanging(false)
    dispatch(
      POST_EMAIL_FEEDBACK_ASYNC.initiate({
        id: activeParticipant.campaignId,
        participantId: activeParticipant.id,
        feedback,
      })
    )
  }

  const showRatedLine = rating && !changing

  return (
    <EmailFooter>
      <EmailFooterCopy>
        <EmailFooterTitle>Rate this simulation email</EmailFooterTitle>
        <EmailFooterDescription>
          Help us improve by rating the quality and realism of this phishing attempt
        </EmailFooterDescription>
      </EmailFooterCopy>
      {showRatedLine ? (
        <RatedLine data-testid="Psima_EmailPreview_Rated_div" role="status">
          <TickCircle size={16} variant="Bold" />
          <span>
            {justRated ? 'Thanks, noted.' : 'You rated this simulation'}{' '}
            <strong>{RATING_LABELS[rating]}</strong>
          </span>
          <span aria-hidden="true">·</span>
          <ChangeRating
            type="button"
            id="Psima_EmailPreview_ChangeRating_button"
            data-testid="Psima_EmailPreview_ChangeRating_button"
            onClick={() => setChanging(true)}
          >
            Change
          </ChangeRating>
        </RatedLine>
      ) : (
        <EmailFooterButtonContainer>
          <Button
            id="Psima_GoodSimulation_button"
            dataTestId="Psima_GoodSimulation_button"
            dataPendoFeature={PSIMA_PENDO_FEATURES.LAUNCHED_FEEDBACK_UP}
            size={SIZES.LARGE}
            variant={
              pendingFeedback === 'up' || rating === 'up' ? VARIANTS.PRIMARY : VARIANTS.SECONDARY
            }
            ariaPressed={rating === 'up'}
            leftElement={
              <Like1
                size={16}
                variant={pendingFeedback === 'up' || rating === 'up' ? 'Bold' : 'Outline'}
              />
            }
            disabled={emailPreviewLoader || emailFeedbackLoader}
            loading={pendingFeedback === 'up'}
            onClick={() => handleFeedback(true)}
          >
            Good simulation
          </Button>
          <Button
            id="Psima_NeedsImprovement_button"
            dataTestId="Psima_NeedsImprovement_button"
            dataPendoFeature={PSIMA_PENDO_FEATURES.LAUNCHED_FEEDBACK_DOWN}
            size={SIZES.LARGE}
            variant={
              pendingFeedback === 'down' || rating === 'down'
                ? VARIANTS.PRIMARY
                : VARIANTS.SECONDARY
            }
            ariaPressed={rating === 'down'}
            leftElement={
              <Dislike
                size={16}
                variant={pendingFeedback === 'down' || rating === 'down' ? 'Bold' : 'Outline'}
              />
            }
            disabled={emailPreviewLoader || emailFeedbackLoader}
            loading={pendingFeedback === 'down'}
            onClick={() => handleFeedback(false)}
          >
            Needs improvement
          </Button>
        </EmailFooterButtonContainer>
      )}
    </EmailFooter>
  )
}

/** Drawer body for the email-review view: the delivered email itself. */
const EMAIL_BODY_SANITIZE = { ALLOWED_URI_REGEXP: /^(https?:)/i }

const EmailPreviewBody = () => {
  const { emailPreviewLoader, emailPreviewData } = useSelector(selectSimulationData)
  const emailBodyRef = useRef(null)
  const sanitizedBody = useMemo(
    () => DOMPurify.sanitize(emailPreviewData.body || '', EMAIL_BODY_SANITIZE),
    [emailPreviewData.body]
  )

  useEffect(() => {
    const container = emailBodyRef.current
    if (!container) {
      return
    }

    container.querySelectorAll('a[href]').forEach((anchor) => {
      anchor.setAttribute('target', '_blank')
      anchor.setAttribute('rel', 'noopener noreferrer')
    })
  }, [emailPreviewLoader, sanitizedBody])

  const senderName = useMemo(() => {
    if (!emailPreviewData.sender) {
      return ''
    }

    return emailPreviewData.sender
      .split(' <')[0]
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }, [emailPreviewData])

  const shortName = useMemo(
    () =>
      senderName
        .split(' ')
        .map((word) => word[0])
        .join(''),
    [senderName]
  )

  const sendingDate = useMemo(() => {
    if (emailPreviewData.sentAt) {
      return formateDesignSystemDate(new Date(emailPreviewData.sentAt), {
        withYear: true,
        monthFirst: true,
        withMinutes: true,
      })
    }
    return ''
  }, [emailPreviewData.sentAt])

  if (emailPreviewLoader) {
    return (
      <SkeletonContainer>
        <Skeleton variant="title" width="100%" height="100%" />
        <Skeleton variant="title" width="50%" height="100%" />
        <div
          style={{
            marginBottom: '50px',
          }}
        >
          <Skeleton variant="title" width="25%" height="100%" />
        </div>
        <Skeleton variant="text" width="100%" height="100%" />
        <Skeleton variant="text" width="75%" height="100%" />
        <Skeleton variant="text" width="50%" height="100%" />
      </SkeletonContainer>
    )
  }

  return (
    <div>
      <EmailHeader>
        <UserInfo>
          <UserShortName data-testid="Psima_EmailPreview_UserShortName_div">
            {shortName}
          </UserShortName>
          <div>
            <UserName data-testid="Psima_EmailPreview_UserName_div">{senderName}</UserName>
            <UserEmail data-testid="Psima_EmailPreview_UserEmail_div">
              {emailPreviewData.sender}
            </UserEmail>
          </div>
        </UserInfo>
        <UserSendingDate data-testid="Psima_EmailPreview_UserSendingDate_div">
          <span>{sendingDate}</span>
          {emailPreviewData.sentLanguage && (
            <SentLanguage data-testid="Psima_EmailPreview_SentLanguage_div">
              <Translate size={14} variant="Outline" />
              {`Sent in ${emailPreviewData.sentLanguage}`}
            </SentLanguage>
          )}
        </UserSendingDate>
      </EmailHeader>
      <EmailTitle data-testid="Psima_EmailPreview_EmailTitle_div">
        {emailPreviewData.title}
      </EmailTitle>
      <EmailBody
        ref={emailBodyRef}
        data-testid="Psima_EmailPreview_EmailBody_div"
        dangerouslySetInnerHTML={{ __html: sanitizedBody }}
      />
    </div>
  )
}

export default EmailPreviewBody
